import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import fp from "lodash/fp";

/* cores */
import convertUtcToLocal from "../../../cores/convertUtcToLocal";
import { sessionKeeper } from "../../../cores/sessionKeeper";

/* modules */
import { setProject } from "../../../modules/project";
import { getProjects } from "../../../modules/user";

/* components */
import WidgetWrap from "./components/wrap";
import WidgetHead from "./components/head";
import StatusBadge from "../../statusbadge";
import TooltipUserInfo from "../../tooltip/userInfo";
import { siteMode } from "../../../cores/siteMode";
import { IconCheckDefault, IconSwapHorizCircle, IconRefresh } from "@mzc-cloudplex/icons";

/* utils */
import { checkIsMobile } from "../../../utils/checkmobile";
import menuService from "@services/menuService";

const isMobile = checkIsMobile();

const AccessibleProjects = ({ name, title, color, icon, removable, onMove, onDelete }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { t } = useTranslation();
    const stateSelector = createSelector(
        (state) => state.stage,
        (state) => state.project,
        (stage, project) => ({
            activeProjectId: fp.get(`id`, project),
            projects: fp.get(`projects`, stage),
            pending: fp.get(`pending`, stage),
            userSiteAccess: fp.get(`user.siteAccess`, stage),
            stage,
        }),
    );
    const state = useSelector(stateSelector);
    const { spaceId, projectId } = menuService.parse(pathname);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);

    const setActiveProject = useCallback(
        (id) => {
            return fp.isEqual(state.activeProjectId, id) ? `Accessed` : `Access`;
        },
        [state.projects, state.activeProjectId],
    );

    const getByName = useCallback((project) => {
        const createdByName = fp.get(`createdByName`, project);
        const updatedByName = fp.get(`updatedByName`, project);

        return fp.isEmpty(createdByName) ? updatedByName : createdByName;
    }, []);

    const getByUserName = useCallback((project) => {
        const createdByUsername = fp.get(`createdByUsername`, project);
        const updatedByUsername = fp.get(`updatedByUsername`, project);

        return fp.isEmpty(createdByUsername) ? updatedByUsername : createdByUsername;
    }, []);

    const handleChangeProject = useCallback(
        (project) => {
            if (fp.isEqual(`Accessed`, setActiveProject(fp.get(`id`, project)))) return false;
            setLoading(true);
            sessionKeeper.set({ stage: state.stage, project });
            const sessionCookie = sessionKeeper.get();
            dispatch(setProject(project, sessionCookie.authProvider));
            history.push(`${menuService.paths.dashboard(spaceId, fp.get(`id`, project))}`);
            setTimeout(() => setLoading(false), 300);
        },
        [dispatch, history, state.stage, setActiveProject],
    );

    useEffect(() => {
        if (!fp.isEmpty(state.activeProjectId)) {
            const composer = fp.pipe(
                // fp.sortBy((project) => {
                //     return !fp.isEqual(fp.get(`id`, project), state.activeProjectId);
                // }),
                setProjects,
            );
            composer(state.projects);
        }
    }, [state.activeProjectId, state.projects]);

    useEffect(
        () => () => {
            setProjects([]);
        },
        [],
    );

    const onMoreView = useCallback(() => {
        history.push(`${menuService.paths.admin.projects(spaceId)}`);
    }, []);

    const onClickRefresh = useCallback(() => {
        dispatch(getProjects());
    }, []);

    return (
        <WidgetWrap id={name} onMove={onMove}>
            <WidgetHead
                title={title}
                icon={icon}
                color={color}
                name={name}
                removable={removable}
                onDelete={onDelete}
                onMoreView={
                    (state.userSiteAccess || []).includes("CONSOLE") && siteMode.get() === "CONSOLE"
                        ? onMoreView
                        : undefined
                }
            />
            <div className={"body"}>
                <div className={"inner"}>
                    {((loading || fp.get(`pending`, state)) && (
                        <div className={"loading-block"}>
                            <span className={"loading-content"} />
                        </div>
                    )) || (
                        <>
                            <div className="control">
                                <div className="control-status">
                                    <span>
                                        {t(`common::label::Total`, "Total")}: <strong>{fp.get(`projects.length`, state)}</strong>
                                    </span>
                                </div>
                                <div className="control-tools">
                                    <button className="btn btn-icon-solid" onClick={onClickRefresh}>
                                        <IconRefresh size={16} />
                                    </button>
                                </div>
                            </div>
                            <div className="table table-responsive table-responsive-x ">
                                <table className="table table-hover">
                                    <colgroup>
                                        <col />
                                        <col width="150px" />
                                        <col width="100px" />
                                        <col width="120px" />
                                    </colgroup>
                                    <tbody>
                                        {fp.map.convert({ cap: false })(
                                            (item, index) => (
                                                <tr
                                                    key={index}
                                                    className={`${
                                                        fp.isEqual(state.activeProjectId, fp.get(`id`, item))
                                                            ? "is-active"
                                                            : ""
                                                    }`}
                                                >
                                                    <td
                                                        className="fit-content"
                                                        data-title={t(`common::label::${"Projects"}`)}
                                                    >
                                                        <div className="list-content">
                                                            <div className="list-title">
                                                                <p>
                                                                    <span className="text-ellipsis">
                                                                        {fp.get(`name`, item)}
                                                                    </span>
                                                                </p>
                                                                <small>{fp.get(`id`, item)}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    {/* <td>
                                                        <StatusBadge status={fp.get(`isActive`, item) ? `ACTIVE` : `INACTIVE`} />
                                                    </td> */}
                                                    <td className="" data-title={t(`common::label::${"Owner"}`)}>
                                                        <div className="list-content">
                                                            <div className="list-title">
                                                                <p>
                                                                    <span className="text-ellipsis">
                                                                        {getByName(item)}
                                                                    </span>
                                                                </p>
                                                                <small>{getByUserName(item)}</small>
                                                                <TooltipUserInfo
                                                                    target={item}
                                                                    id={fp.get(`createdById`, item)}
                                                                />
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td
                                                        className="text-wrap"
                                                        data-title={t(`common::label::${"Expired date"}`)}
                                                    >
                                                        <div>
                                                            {!isMobile && (
                                                                <span className="text-smaller text-secondary">
                                                                    {t(`common::label::${"Expired date"}`)}
                                                                </span>
                                                            )}
                                                            <p>
                                                                {fp.get(`expiredAt`, item)
                                                                    ? convertUtcToLocal(fp.get(`expiredAt`, item))
                                                                    : "-"}
                                                            </p>
                                                        </div>
                                                    </td>
                                                    <td className="status">
                                                        <button
                                                            type={"button"}
                                                            className={`${
                                                                fp.isEqual(state.activeProjectId, fp.get(`id`, item))
                                                                    ? "btn btn-primary btn-round btn-xs"
                                                                    : "btn btn-transparent"
                                                            } ${
                                                                fp.isEqual(
                                                                    `Accessed`,
                                                                    setActiveProject(fp.get(`id`, item)),
                                                                )
                                                                    ? `read-only`
                                                                    : ``
                                                            }`}
                                                            onClick={() => handleChangeProject(item)}
                                                        >
                                                            {fp.isEqual(state.activeProjectId, fp.get(`id`, item)) && (
                                                                <IconCheckDefault size={12} />
                                                            )}

                                                            {!fp.isEqual(state.activeProjectId, fp.get(`id`, item)) && (
                                                                <IconSwapHorizCircle size={16} htmlColor={`#83839a`} />
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ),
                                            projects,
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </WidgetWrap>
    );
};

AccessibleProjects.propTypes = {
    name: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.node,
    removable: PropTypes.bool,
    onMove: PropTypes.func,
    onDelete: PropTypes.func,
};

export default AccessibleProjects;
